import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Youtube, Linkedin, Mail, ArrowUp, Gift, Copy, Check, LogIn } from 'lucide-react';

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
import { useAppSelector, selectIsAuthenticated, selectCurrentUser, useGetCreditConfigQuery, useGetSocialLinksQuery } from '@nimbazaar/store';

const exploreLinks = [
  { label: 'Home', to: '/' },
  { label: 'Browse Ads', to: '/ads' },
  { label: 'Post Ad', to: '/post-ad' },
];

const accountLinks = [
  { label: 'My Ads', to: '/account/my-ads' },
  { label: 'My Bids', to: '/account/my-bids' },
  { label: 'Wallet', to: '/account/wallet' },
  { label: 'Profile', to: '/account/profile' },
  { label: 'Inbox', to: '/inbox' },
];

function PinterestIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
    </svg>
  );
}

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.75a8.18 8.18 0 004.76 1.52V6.84a4.84 4.84 0 01-1-.15z" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

const PLATFORM_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  linkedin: Linkedin,
  x: XIcon,
  youtube: Youtube,
  instagram: Instagram,
  facebook: FacebookIcon,
  tiktok: TikTokIcon,
  pinterest: PinterestIcon,
};

const PLATFORM_NAMES: Record<string, string> = {
  linkedin: 'LinkedIn',
  x: 'X',
  youtube: 'YouTube',
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  pinterest: 'Pinterest',
};

const DEFAULT_SOCIAL = [
  { platform: 'linkedin', url: 'https://www.linkedin.com/in/nimbazaar' },
  { platform: 'x', url: 'https://x.com/NimBazaar' },
  { platform: 'youtube', url: 'https://www.youtube.com/@NimBazaar' },
  { platform: 'instagram', url: 'https://www.instagram.com/nimbazaar/' },
];

export function Footer() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectCurrentUser);
  const { data: creditConfigData } = useGetCreditConfigQuery();
  const { data: socialData } = useGetSocialLinksQuery();
  const socialLinks = socialData?.data ?? DEFAULT_SOCIAL;
  const referralBonus = creditConfigData?.data?.REFERRAL_BONUS ?? 50;
  const referralLink = user ? `https://nimbazaar.com/?ref=${user.id}` : '';
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-primary-900 text-white relative">
      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className="absolute right-4 sm:right-8 -top-6 w-12 h-12 bg-secondary-500 hover:bg-secondary-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
        aria-label="Back to top"
      >
        <ArrowUp className="w-5 h-5 text-white" />
      </button>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
          {/* Branding */}
          <div className="col-span-2 md:col-span-1">
            <img src="/logo.png" alt="NimBazaar" className="h-16 w-auto mb-4 drop-shadow-lg" />
            <p className="text-white/60 text-sm leading-relaxed mb-5 max-w-xs">
              Post what you need and let verified sellers come to you with the best offers.
            </p>
            {/* Social */}
            <div className="flex items-center gap-2">
              {socialLinks.map((social, i) => {
                const IconComponent = PLATFORM_ICONS[social.platform];
                if (!IconComponent) return null;
                return (
                  <a
                    key={`${social.platform}-${i}`}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-white/10 hover:bg-secondary-500 rounded-full flex items-center justify-center transition-colors"
                    aria-label={PLATFORM_NAMES[social.platform] || social.platform}
                  >
                    <IconComponent className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Explore */}
          <div>
            <h3 className="font-semibold text-white text-sm uppercase tracking-wider mb-4">Explore</h3>
            <ul className="space-y-2.5">
              {exploreLinks.map((link) => (
                <li key={link.label}>
                  <Link to={link.to} className="text-white/60 hover:text-secondary-400 text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Account */}
          <div>
            <h3 className="font-semibold text-white text-sm uppercase tracking-wider mb-4">Account</h3>
            <ul className="space-y-2.5">
              {accountLinks.map((link) => (
                <li key={link.label}>
                  <Link to={link.to} className="text-white/60 hover:text-secondary-400 text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + Refer/Join */}
          <div className="col-span-2 md:col-span-1">
            <h3 className="font-semibold text-white text-sm uppercase tracking-wider mb-4">Contact</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5 text-white/60 text-sm">
                <Mail className="w-4 h-4 mt-0.5 text-secondary-400 shrink-0" />
                <span>nimbazaar7@gmail.com</span>
              </li>
            </ul>

            {isAuthenticated && referralLink ? (
              /* Refer & Earn — logged in */
              <div className="mt-5 bg-white/10 rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-4 h-4 text-secondary-400" />
                  <span className="text-sm font-semibold text-white">Refer & Earn</span>
                </div>
                <p className="text-xs text-white/50 mb-3">
                  Get {referralBonus} free credits for each friend you refer!
                </p>
                <div className="flex items-center gap-1.5">
                  <div className="flex-1 bg-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white/40 truncate">
                    {referralLink}
                  </div>
                  <button
                    onClick={handleCopy}
                    className={`shrink-0 flex items-center gap-1 px-2.5 py-1.5 text-white text-xs font-medium rounded-lg transition-colors ${
                      copied ? 'bg-green-500' : 'bg-secondary-500 hover:bg-secondary-600'
                    }`}
                  >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            ) : (
              /* Join & Earn — not logged in */
              <div className="mt-5 bg-white/10 rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-4 h-4 text-secondary-400" />
                  <span className="text-sm font-semibold text-white">Join & Earn</span>
                </div>
                <p className="text-xs text-white/50 mb-3">
                  Sign up now and get free credits to start posting ads!
                </p>
                <Link
                  to="/"
                  state={{ openAuth: true }}
                  className="flex items-center justify-center gap-2 w-full py-2 bg-secondary-500 hover:bg-secondary-600 text-white text-xs font-medium rounded-lg transition-colors"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  Sign Up / Login
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/40 text-xs sm:text-sm">
            &copy; {new Date().getFullYear()} NimBazaar. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" className="text-white/40 hover:text-white text-xs sm:text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="text-white/40 hover:text-white text-xs sm:text-sm transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
